/**
 * MenuSchema.ts
 *
 * Platform-agnostic, typed description of a menu panel.
 * This is the single source of truth for menu structure and behaviour.
 *
 * Neither HTML nor VR rendering logic lives here — renderers read this and
 * produce the appropriate output for their target platform.
 *
 * All text/value fields accept either a static value or a zero-argument
 * closure so that dynamic state (e.g. device names, connection flags) can be
 * captured at render/update time without coupling the schema to any renderer.
 */

// ─── Leaf nodes ─────────────────────────────────────────────────────────────

export type DisplayPlatform = 'web' | 'vr';

export interface PlatformVisibility {
    platforms?: DisplayPlatform[];
}

/**
 * A clickable button.
 * On activation the renderer emits `id` via MenuController.onUserChoice.
 */
export interface ButtonNode extends PlatformVisibility {
    type: 'button';
    /** Stable action ID emitted via MenuController.onUserChoice on click. */
    id: string;
    label: string | (() => string);
    hidden?: () => boolean;
    disabled?: () => boolean;
}

/**
 * A labelled toggle (checkbox).
 * On change the renderer emits `id` via MenuController.onUserChoice
 * with payload `{ checked: boolean }`.
 */
export interface ToggleNode extends PlatformVisibility {
    type: 'toggle';
    id: string;
    label: string;
    checked: () => boolean;
    hidden?: () => boolean;
}

/**
 * A horizontal range slider.
 * On change the renderer emits `id` via MenuController.onUserChoice
 * with payload `{ value: number }`.
 */
export interface SliderNode extends PlatformVisibility {
    type: 'slider';
    id: string;
    label: string | (() => string);
    min: number;
    max: number;
    step?: number;
    get: () => number;
    hidden?: () => boolean;
}

/**
 * Static or dynamic display-only text (no interaction).
 */
export interface TextNode extends PlatformVisibility {
    type: 'text';
    text: string | (() => string);
    hidden?: () => boolean;
}

/**
 * A single-line text field.
 * On commit (Enter or blur) the renderer emits `id` via
 * MenuController.onUserChoice with payload `{ value: string }`.
 */
export interface TextInputNode extends PlatformVisibility {
    type: 'input';
    id: string;
    placeholder: string;
    get: () => string;
    inputType?: 'text' | 'password';
    hidden?: () => boolean;
}

// ─── Container nodes ─────────────────────────────────────────────────────────

/**
 * Horizontal or vertical group of child nodes.
 */
export interface GroupNode extends PlatformVisibility {
    type: 'group';
    direction: 'row' | 'column';
    children: MenuNode[];
    hidden?: () => boolean;
}

/**
 * Named section with a visible header label.
 */
export interface SectionNode extends PlatformVisibility {
    type: 'section';
    header: string;
    children: MenuNode[];
    hidden?: () => boolean;
}

/**
 * A list whose rows are computed at render time.
 * `items()` returns an array of rows; each row is an array of MenuNodes.
 * This covers dynamic content such as per-device slider/button sets.
 */
export interface DynamicListNode extends PlatformVisibility {
    type: 'list';
    items: () => MenuNode[][];
    hidden?: () => boolean;
}

// ─── Union ───────────────────────────────────────────────────────────────────

export type MenuNode =
    | ButtonNode
    | ToggleNode
    | SliderNode
    | TextNode
    | TextInputNode
    | GroupNode
    | SectionNode
    | DynamicListNode;

// ─── Schema ──────────────────────────────────────────────────────────────────

/**
 * Complete description of a single menu panel (submenu).
 * Contains no rendering logic — renderers interpret this structure.
 */
export interface MenuSchema extends PlatformVisibility {
    /**
     * Unique identifier; should match the corresponding Submenu enum value
     * so renderers and presenters can be correlated without extra mappings.
     */
    id: string;
    /** Human-readable title shown in the panel header. */
    title: string;
    /** Top-level nodes rendered top-to-bottom in a vertical column. */
    nodes: MenuNode[];
}

export function isVisibleOnPlatform(target: PlatformVisibility | undefined, platform: DisplayPlatform): boolean {
    if (!target?.platforms || target.platforms.length === 0) {
        return true;
    }

    return target.platforms.includes(platform);
}

export function isSchemaVisibleOnPlatform(schema: MenuSchema | undefined, platform: DisplayPlatform): boolean {
    return isVisibleOnPlatform(schema, platform);
}

export function isNodeVisibleOnPlatform(node: MenuNode | undefined, platform: DisplayPlatform): boolean {
    return isVisibleOnPlatform(node, platform);
}

export function findMenuNodeById(schema: MenuSchema, id: string): MenuNode | undefined {
    return schema.nodes.find((node) => (node as { id?: string }).id === id);
}
